// 데이터 조회 기간 — 통계 단위(시간/일/주/월)별 KST 달력 기간과 x축 버킷.
//
// 서버는 time_bucket(…, 'Asia/Seoul') 로 KST 자정·월요일·1일 경계에 맞춰 집계한다.
// 여기서도 같은 경계를 브라우저 타임존과 무관하게 계산해야 버킷이 1:1 로 맞는다.
// KST 는 DST 가 없어 고정 +9h 오프셋으로 이동한 뒤 UTC 게터로 달력 값을 읽는다.

export type Bucket = 'hour' | 'day' | 'week' | 'month';

export const BUCKETS: readonly Bucket[] = ['hour', 'day', 'week', 'month'];

/** 서버 /api/sensors/timeseries 의 interval 값 */
export const BUCKET_INTERVAL: Record<Bucket, string> = {
  hour: '1 hour',
  day: '1 day',
  week: '1 week',
  month: '1 month',
};

export const BUCKET_LABEL: Record<Bucket, string> = {
  hour: '시간별',
  day: '일별',
  week: '주별',
  month: '월별',
};

/** 주별 보기에 표시하는 주 수 */
const WEEKS_PER_PERIOD = 12;

/** 표시 기간. `to` 는 미포함(다음 기간 시작). */
export interface PeriodRange {
  from: Date;
  to: Date;
}

const KST_OFFSET = 9 * 3_600_000;
const DAY = 86_400_000;
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;

interface KstParts {
  y: number;
  /** 0-11 */
  m: number;
  d: number;
  h: number;
  /** 0=일 … 6=토 */
  wd: number;
}

function kst(date: Date): KstParts {
  const s = new Date(date.getTime() + KST_OFFSET);
  return {
    y: s.getUTCFullYear(), m: s.getUTCMonth(), d: s.getUTCDate(), h: s.getUTCHours(), wd: s.getUTCDay(),
  };
}

/** KST 달력 값 → 실제 시각. 월·일 넘침은 Date.UTC 가 정규화한다. */
function fromKst(y: number, m: number, d = 1, h = 0): Date {
  return new Date(Date.UTC(y, m, d, h) - KST_OFFSET);
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** 앵커가 속한 KST 주의 월요일 00:00 */
function weekStart(anchor: Date): Date {
  const p = kst(anchor);
  return fromKst(p.y, p.m, p.d - ((p.wd + 6) % 7));
}

export function periodRange(bucket: Bucket, anchor: Date): PeriodRange {
  const p = kst(anchor);
  switch (bucket) {
    case 'hour':
      return { from: fromKst(p.y, p.m, p.d), to: fromKst(p.y, p.m, p.d + 1) };
    case 'day':
      return { from: fromKst(p.y, p.m), to: fromKst(p.y, p.m + 1) };
    case 'week': {
      const to = new Date(weekStart(anchor).getTime() + 7 * DAY);
      return { from: new Date(to.getTime() - WEEKS_PER_PERIOD * 7 * DAY), to };
    }
    case 'month':
      return { from: fromKst(p.y, 0), to: fromKst(p.y + 1, 0) };
  }
}

/** 이전(-1)/다음(+1) 기간에 속하는 앵커 */
export function shiftAnchor(bucket: Bucket, anchor: Date, dir: -1 | 1): Date {
  const { from } = periodRange(bucket, anchor);
  const p = kst(from);
  switch (bucket) {
    case 'hour':
      return fromKst(p.y, p.m, p.d + dir);
    case 'day':
      return fromKst(p.y, p.m + dir);
    case 'week':
      return new Date(periodRange(bucket, anchor).to.getTime() - DAY + dir * WEEKS_PER_PERIOD * 7 * DAY);
    case 'month':
      return fromKst(p.y + dir, 0);
  }
}

/** 현재 시각을 포함(또는 미래)하는 기간이면 true — 다음 기간으로 이동할 수 없다. */
export function isLatestPeriod(bucket: Bucket, anchor: Date, now: Date = new Date()): boolean {
  return periodRange(bucket, anchor).to.getTime() > now.getTime();
}

function ymd(date: Date): string {
  const p = kst(date);
  return `${p.y}.${pad2(p.m + 1)}.${pad2(p.d)}`;
}

function md(date: Date): string {
  const p = kst(date);
  return `${pad2(p.m + 1)}.${pad2(p.d)}`;
}

/** 기간 이동 컨트롤에 표시하는 기간 이름 */
export function periodLabel(bucket: Bucket, anchor: Date): string {
  const { from, to } = periodRange(bucket, anchor);
  const p = kst(from);
  switch (bucket) {
    case 'hour':
      return `${ymd(from)} (${WEEKDAYS[p.wd]})`;
    case 'day':
      return `${p.y}년 ${p.m + 1}월`;
    case 'week':
      return `${ymd(from)} ~ ${md(new Date(to.getTime() - DAY))}`;
    case 'month':
      return `${p.y}년`;
  }
}

/** 기간 안의 모든 버킷 시작 시각(ISO). 서버 버킷 timestamp 와 같은 값이다. */
export function bucketAxis(bucket: Bucket, range: PeriodRange): string[] {
  const out: string[] = [];
  const end = range.to.getTime();
  const p = kst(range.from);
  for (let i = 0; ; i++) {
    let t: Date;
    switch (bucket) {
      case 'hour': t = fromKst(p.y, p.m, p.d, p.h + i); break;
      case 'day': t = fromKst(p.y, p.m, p.d + i); break;
      case 'week': t = fromKst(p.y, p.m, p.d + 7 * i); break;
      case 'month': t = fromKst(p.y, p.m + i); break;
    }
    if (t.getTime() >= end) return out;
    out.push(t.toISOString());
  }
}

/** x축 눈금 라벨 */
export function bucketTick(bucket: Bucket, iso: string): string {
  const p = kst(new Date(iso));
  switch (bucket) {
    case 'hour': return `${p.h}시`;
    case 'day':
    case 'week': return `${p.m + 1}/${p.d}`;
    case 'month': return `${p.m + 1}월`;
  }
}

/** 툴팁·표·CSV 에 쓰는 버킷 전체 이름 */
export function bucketFull(bucket: Bucket, iso: string): string {
  const date = new Date(iso);
  const p = kst(date);
  switch (bucket) {
    case 'hour': return `${md(date)} ${pad2(p.h)}:00`;
    case 'day': return `${ymd(date)} (${WEEKDAYS[p.wd]})`;
    case 'week': return `${ymd(date)} ~ ${md(new Date(date.getTime() + 6 * DAY))}`;
    case 'month': return `${p.y}년 ${p.m + 1}월`;
  }
}

type Row = Record<string, number | string>;

/**
 * 통계 row 를 x축 버킷에 맞춰 편다. 값이 없는 버킷은 `{ ts }` 만 남겨
 * (connectNulls 를 끈 차트에서) 선이 끊기고 결측 기간이 가로 폭을 차지하게 한다.
 * 서버 time_bucket 은 측정이 없는 버킷을 만들지 않으므로 이 정렬이 결측을 드러낸다.
 */
export function alignToAxis(rows: Row[], axis: string[]): Row[] {
  const byTime = new Map<number, Row>();
  for (const r of rows) byTime.set(new Date(r.ts as string).getTime(), r);
  return axis.map((ts) => {
    const hit = byTime.get(new Date(ts).getTime());
    return hit ? { ...hit, ts } : { ts };
  });
}
